// @system — Blog helpers: API shape mapping, date formatting, filtering

// ── API shape (mirrors DB) ────────────────────────────────────────────────────

/**
 * A `blog_posts` row as GET /blog and GET /blog/:slug return it (snake_case) — the one
 * definition lives next to the fetchers in api/@system/blog.js.
 * @typedef {import('../../../../api/@system/blog.js').BlogPostRow} ApiBlogPost
 */

/**
 * Post as the blog pages render it (camelCase; also the shape of the BLOG_POSTS seed data).
 * Seed posts carry none of the optional media/source fields — `isStaticPost()` tells them apart
 * from rows the API returned.
 * @typedef {object} BlogPost
 * @property {string} id
 * @property {string} slug
 * @property {string} title
 * @property {string} excerpt
 * @property {string} content         markdown subset (MarkdownContent renderer; fallback when contentHtml is empty)
 * @property {string} category
 * @property {string} author
 * @property {string} publishedAt     ISO date
 * @property {number | null} readingTime     minutes
 * @property {string[]} [tags]
 * @property {string | null} [metaTitle]         GET /blog/:slug only
 * @property {string | null} [metaDescription]   GET /blog/:slug only
 * @property {string | null} [contentHtml]       Sanitized HTML rendered server-side (GET /blog/:slug only)
 * @property {string | null} [coverImage]        1600×900 hero
 * @property {string | null} [thumbnailImage]    640×360 card image
 * @property {string | null} [ogImage]           1200×630 social card
 * @property {import('../../../../api/@system/blog.js').BlogImage[]} [images]
 * @property {import('../../../../api/@system/blog.js').BlogSource} [source]  absent on seed data
 * @property {string | null} [keyword]
 * @property {string | null} [canonicalUrl]
 */

/**
 * @param {ApiBlogPost} p
 * @returns {BlogPost}
 */
export function apiPostToBlogPost(p){
  return {
    id: String(p.id),
    slug: p.slug,
    title: p.title,
    excerpt: p.excerpt ?? '',
    content: p.content ?? '',
    category: p.category ?? '',
    author: p.author ?? '',
    publishedAt: p.published_at ?? p.created_at,
    readingTime: p.reading_time ?? null,
    tags: p.tags ?? [],
    metaTitle: p.meta_title ?? null,
    metaDescription: p.meta_description ?? null,
    contentHtml: p.content_html ?? null,
    coverImage: p.cover_image ?? null,
    thumbnailImage: p.thumbnail_image ?? null,
    ogImage: p.og_image ?? null,
    images: Array.isArray(p.images) ? p.images : [],
    source: p.source ?? 'manual',
    keyword: p.keyword ?? null,
    canonicalUrl: p.canonical_url ?? null }
}

/**
 * True for the bundled BLOG_POSTS seed data (no DB row behind it): related posts and other
 * per-post API calls have nothing to fetch for these.
 * @param {BlogPost} post
 */
export function isStaticPost(post){
  return post.source === undefined
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * "January 15, 2024" for a date-only or ISO timestamp string. Locale AND time zone are pinned:
 * the same string is rendered by the server (lib/@system/Ssr) and by the hydrating browser, and a
 * host in another zone would otherwise print the previous/next day for a midnight timestamp and
 * make React throw the server HTML away.
 * @param {string|number|Date} iso
 * @returns {string}
 */
export function formatDate(iso){
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC' })
}

/**
 * @param {BlogPost[]} posts
 * @param {string} category   'All' keeps every category
 * @param {string} query
 * @returns {BlogPost[]}
 */
export function filterPosts(posts, category, query){
  let filtered = posts
  if (category !== 'All') {
    filtered = filtered.filter((p) => p.category === category)
  }
  if (query.trim()) {
    const q = query.toLowerCase()
    filtered = filtered.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        (p.tags ?? []).some((t) => t.toLowerCase().includes(q)),
    )
  }
  return filtered
}
